import { getConfigUrl } from '@config'
import { ROUTES } from '@constants/app_routes'
import { GaugeKey, MeterId, MeterType } from '@constants/gauges_meters_consts'
import type { AppState, MainDataType } from '@constants/types'
import type { TemperatureUnit } from '@constants/utils'
import { getRealVesselMaxSpeed } from '@constants/utils'
import type { Metric } from '@shared_types/vessel_data'
import { DEGREE_SYMBOL } from '@utils/string_utils'
import { useTemperatureConverter } from '@utils/unit_utils'
import { min } from 'lodash'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

const convertMetersToKilometers = (value: number) => value / 1000

interface IGaugeItemValue {
  id: string
  type: string
  value: number | object
  prevValue?: number
  label?: string
  formatter?: (v: number) => string
  unit?: string
  maxValue?: number
  minValue?: number
}

interface ShowInOrder {
  active?: boolean
  order?: number
}

interface IShowGaugeProps {
  data: Metric[]
  showDepth?: ShowInOrder
  showEcoScore?: ShowInOrder
  showEarth?: ShowInOrder
  showMecca?: ShowInOrder
  showOrientation?: ShowInOrder
  showPressure?: ShowInOrder
  showSpeed?: ShowInOrder
  showTime?: ShowInOrder
  showWeather?: ShowInOrder
  showWind?: ShowInOrder
  tempUnit: TemperatureUnit
}

interface IStateItems {
  sunRoad: {
    sunrise: number
    sunset: number
  }
  vesselMaxSpeed: number | string
  vesselMaxDepth: number | string
  meccaLocation: unknown
  meccaDistance: number
  world: unknown
}

export default function useDashboard (main: MainDataType, showGaugeProps: IShowGaugeProps, stateItems: IStateItems) {
  const vesselMaxSpeed = getRealVesselMaxSpeed(stateItems.vesselMaxSpeed)
  const vesselMaxDepth = getRealVesselMaxSpeed(stateItems.vesselMaxDepth)

  const tempConv = useTemperatureConverter(showGaugeProps.tempUnit)

  const { value } = useSelector((state: AppState) => state.time)

  return useMemo(() => {
    let gaugeOrder = 1

    return [
      {
        key: GaugeKey.TIME,
        label: 'Time',
        path: ROUTES.TIMES,
        values: showGaugeProps.showTime?.active ? [{
          id: MeterId.LOCALE_CLOCK,
          type: MeterType.TIME,
          maxValue: 1,
          value: moment(value).isValid() ? moment(value) : moment(),
          sunrise: stateItems.sunRoad.sunrise,
          sunset: stateItems.sunRoad.sunset
        } as unknown as IGaugeItemValue] : [],
        order: showGaugeProps.showTime?.order || gaugeOrder++
      },
      {
        key: GaugeKey.SPEED,
        label: 'Speed',
        path: ROUTES.SPEED_DISTANCE,
        icon: getConfigUrl('icons.yacht'),
        values: showGaugeProps.showSpeed?.active ? [{
          id: MeterId.SPEED,
          type: MeterType.SPEED,
          value: main.speed.value,
          maxValue: vesselMaxSpeed,
          formatter: (v: number) => v.toFixed(1),
          unit: 'kn'
        }] : [],
        order: showGaugeProps.showSpeed?.order || gaugeOrder++
      },
      {
        key: GaugeKey.DISTANCE,
        label: '',
        path: ROUTES.DISTANCE,
        icon: getConfigUrl('icons.yacht'),
        values: showGaugeProps.showOrientation?.active ? [{
          id: MeterId.HEADING,
          type: MeterType.HEADING,
          value: min([main.heading.value, 360]) as number,
          windDirection: main.windDirection.value,
          maxValue: 360,
          meccaLocation: stateItems.meccaLocation,
          unit: DEGREE_SYMBOL
        } as unknown as IGaugeItemValue] : [],
        order: showGaugeProps.showOrientation?.order || gaugeOrder++
      },
      {
        key: GaugeKey.DEPTH,
        label: 'Depth',
        path: ROUTES.DEPTH,
        values: showGaugeProps.showDepth?.active ? [{
          id: MeterId.DEPTH,
          type: MeterType.DEPTH,
          value: main.depth.value,
          maxValue: vesselMaxDepth,
          data: showGaugeProps.data,
          unit: 'm'
        } as unknown as IGaugeItemValue] : [],
        order: showGaugeProps.showDepth?.order || gaugeOrder++
      },
      {
        key: GaugeKey.PRESSURE,
        label: 'Pressure',
        path: ROUTES.BAROMETRIC_PRESSURE,
        values: showGaugeProps.showPressure?.active ? [{
          id: MeterId.PRESSURE,
          type: MeterType.PRESSURE,
          value: main.pressure.value,
          minValue: 960,
          maxValue: 1060,
          unit: 'hPa'
        }] : [],
        order: showGaugeProps.showPressure?.order || gaugeOrder++
      },
      {
        key: GaugeKey.WIND,
        label: 'Wind',
        path: ROUTES.WIND,
        values: showGaugeProps.showWind?.active ? [{
          id: MeterId.WIND,
          type: MeterType.WIND,
          value: main.windDirection.value,
          windSpeed: main.windSpeed.value,
          maxValue: 360,
          unit: DEGREE_SYMBOL,
          windSpeedUnit: 'kn'
        } as unknown as IGaugeItemValue] : [],
        order: showGaugeProps.showWind?.order || gaugeOrder++
      },
      {
        key: GaugeKey.TEMPERATURE,
        label: 'Temperature',
        path: ROUTES.TEMPERATURES,
        values: showGaugeProps.showWeather?.active ? [{
          id: MeterId.TEMPERATURE,
          type: MeterType.TEMPERATURE,
          value: +tempConv(main.airTemperature.value),
          seaTemp: +tempConv(main.seaWaterTemperature.value),
          maxValue: +tempConv(25),
          formatter: (v: number) => v.toFixed(1),
          unit: showGaugeProps.tempUnit
        } as unknown as IGaugeItemValue] : [],
        order: showGaugeProps.showWeather?.order || gaugeOrder++
      },
      {
        key: GaugeKey.MECCA,
        label: 'Makkah',
        path: ROUTES.MEKKAH,
        values: showGaugeProps.showMecca?.active ? [{
          id: MeterId.MECCA,
          type: MeterType.MECCA,
          value: min([main.heading.value, 360]) as number,
          maxValue: 360,
          meccaLocation: stateItems.meccaLocation,
          meccaDistance: convertMetersToKilometers(stateItems.meccaDistance),
          unit: 'km'
        } as unknown as IGaugeItemValue] : [],
        order: showGaugeProps.showMecca?.order || gaugeOrder++
      },
      {
        key: GaugeKey.MAP,
        label: 'Location',
        path: ROUTES.MAP,
        values: showGaugeProps.showEarth?.active ? [{
          id: MeterId.EARTH,
          type: MeterType.EARTH,
          maxValue: 1,
          value: main.position.value,
          lat: main.position.value.lat,
          long: main.position.value.lng,
          time: main.position.timeString,
          worldMap: stateItems.world
        } as unknown as IGaugeItemValue] : [],
        order: showGaugeProps.showEarth?.order || gaugeOrder++,
        from: 'dashboard'
      },
      {
        key: GaugeKey.ECO_SCORE,
        label: 'Eco score',
        path: ROUTES.ECO_SCORE,
        values: showGaugeProps.showEcoScore?.active ? [{
          id: MeterId.ECO_SCORE,
          type: MeterType.ECO_SCORE,
          value: main.ecoScore.value,
          maxValue: 100,
          formatter: (v: number) => v.toFixed(1),
          unit: '%'
        }] : [],
        order: showGaugeProps.showEcoScore?.order || gaugeOrder++,
        from: 'dashboard'
      }
    ]
  }, [
    main.speed.value,
    main.heading.value,
    main.windDirection.value,
    main.ecoScore.value,
    main.depth.value,
    main.pressure.value,
    main.windSpeed.value,
    main.airTemperature.value,
    main.seaWaterTemperature.value,
    main.position.value,
    stateItems.meccaDistance,
    stateItems.world,
    value
  ])
}
